import { Link } from '@tanstack/react-router';
import { Check, ChevronRight, RefreshCw, Search, X } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { CopyIconButton } from '~/components/common/copy-icon-button';
import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '~/components/ui/popover';
import { cn } from '~/utils/cn';

import { useNewRunFilters } from '../hooks/use-new-run-options';
import { useRun } from '../hooks/use-run';
import { useFunnelStageReasons } from '../hooks/use-run-funnel';
import { useRunTitle } from '../hooks/use-run-title';
import { draftFromTriggerRequest } from '../mappers/new-run.mapper';
import { parseProductIds } from '../types/new-run.types';
import type { RunProgress, RunRead } from '../types/run.api.types';
import { catalogLabel, humanizeCode } from '../utils/catalog-labels';
import { FIELD_GROUP_ICON, fieldIconOf } from '../utils/field-icons';
import {
  fieldGroupOf,
  NEW_RUN_FIELD_GROUP_ORDER,
  type NewRunFieldGroup,
} from '../utils/new-run-field-groups';

import { ConnectorMark } from './connector-mark';

/** Lists longer than this get a search box in their popover. */
const SEARCH_FROM = 8;

/** Rows a popover draws at once — a 10k-id batch narrows by search instead of drawing all. */
const RENDER_CAP = 200;

/** Feed intake's reason code for one trigger filter — `FILTER:<key>` (funnel ②). */
const FILTER_REASON_PREFIX = 'FILTER:';

interface Props {
  runId: string;
  run: RunRead | undefined;
  progress: RunProgress;
  /** Inside a card that already draws the "Settings" title (the Insights rail) — drops the
   *  divider and the card's own heading; the rows are unchanged. */
  embedded?: boolean;
}

/**
 * How the batch was set up, as plain questions and answers: what it does, which products, which
 * content it writes, whether it scores, adds search descriptors or lists skipped products, and how
 * it started. Long lists (fields, filters, ids) open in a searchable popover beside the rail.
 */
export function RunSettingsCard({ runId, run, progress, embedded = false }: Props) {
  const { t } = useTranslation('catalog-enrichment-run');
  const { t: tRuns } = useTranslation('catalog-enrichment-runs');
  const { t: tCatalog } = useTranslation('catalog-enrichment');

  const request = run?.config?.request;
  const connector = progress.connectorCode ?? run?.connectorCode ?? null;
  const flow = progress.flow ?? run?.flow ?? run?.config?.flow?.name ?? null;
  const requestConnector =
    request && typeof request.connectorCode === 'string' ? request.connectorCode : '';
  const filtersQuery = useNewRunFilters(requestConnector);
  const filterDefs = useMemo(() => filtersQuery.data?.filters ?? [], [filtersQuery.data]);
  // The SAME mapper the New batch "copy settings" picker uses, so both read one request alike.
  const draft = useMemo(
    () => (request ? draftFromTriggerRequest(request, filterDefs) : null),
    [request, filterDefs],
  );

  const filters = draft?.filters ?? [];
  const fields = draft?.fields ?? [];
  const productIds = draft?.productIds ? parseProductIds(draft.productIds) : [];
  // Per-filter exclusions come from Feed intake's reasons; read only when the batch has filters.
  const reasonsQuery = useFunnelStageReasons(runId, 'IMPORT', null, filters.length > 0);
  const excludedByFilter = useMemo(() => {
    const counts = new Map<string, number>();
    for (const reason of reasonsQuery.data?.reasons ?? []) {
      if (reason.code.startsWith(FILTER_REASON_PREFIX)) {
        counts.set(reason.code.slice(FILTER_REASON_PREFIX.length), reason.count);
      }
    }
    return counts;
  }, [reasonsQuery.data]);
  const excludedTotal = [...excludedByFilter.values()].reduce((sum, n) => sum + n, 0);

  if (!request && !flow) return null;

  const testId = 'catalog-enrichment-run-setup';
  const maxProducts = request?.maxProducts;
  const steps = draft?.postSteps;
  const filterLabel = (key: string) => filterDefs.find((d) => d.key === key)?.label ?? key;

  // Which products: the import's scope in words, capped when the batch set a maximum.
  const scope = progress.ingestMode
    ? catalogLabel(tCatalog, 'ingestMode', progress.ingestMode)
    : t('setupCard.products.all');
  const productsAnswer =
    typeof maxProducts === 'number' && maxProducts > 0
      ? t('setupCard.products.capped', { scope, count: maxProducts })
      : scope;

  // The fields by name — the first two, then how many more — so the row says what gets written.
  const fieldNames = fields.map((code) => humanizeCode(code));
  const fieldsAnswer =
    fieldNames.length > 2
      ? t('setupCard.fields.someMore', {
          names: fieldNames.slice(0, 2).join(', '),
          count: fieldNames.length - 2,
        })
      : fieldNames.join(', ');

  const pre = steps?.scoringPreEnabled;
  const post = steps?.scoringPostEnabled;
  const scoringKey =
    pre === undefined && post === undefined
      ? null
      : pre && post
        ? 'both'
        : post
          ? 'after'
          : pre
            ? 'before'
            : 'none';

  const descriptors = steps?.searchDescriptorsEnabled;
  const recordSkipped = draft?.recordSkipped;
  const trigger = run?.triggerSource ?? null;

  return (
    <section
      aria-label={t('factsRail.settings')}
      data-testid={testId}
      className={cn('flex flex-col gap-3', !embedded && 'border-t pt-3')}
    >
      {embedded ? null : (
        <h3 className="text-secondary-text text-sm font-semibold">{t('factsRail.settings')}</h3>
      )}

      {/* One question per row with its answer in words, so nothing needs decoding. */}
      <dl className="flex flex-col">
        <SetupRow label={t('setupCard.whatItDoes.label')} testId={`${testId}-what`}>
          {flow === 'FEED_AUDIT' ? (
            t('setupCard.whatItDoes.audit')
          ) : connector ? (
            <span className="inline-flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
              {t('setupCard.whatItDoes.publishTo')}
              <ConnectorMark code={connector} showName className="font-medium" />
            </span>
          ) : (
            t('setupCard.whatItDoes.publish')
          )}
          {flow ? (
            <span className="text-secondary-text mt-0.5 block text-xs">
              {catalogLabel(tCatalog, 'flow', flow)}
            </span>
          ) : null}
        </SetupRow>

        <SetupRow label={t('setupCard.products.label')} testId={`${testId}-products`}>
          {productsAnswer}
          {filters.length > 0 ? (
            <SettingPopoverRow
              testId={`${testId}-filters-button`}
              summary={
                <>
                  {t('setupCard.products.filtered', { count: filters.length })}
                  {excludedTotal > 0 ? (
                    <span className="text-error">
                      {' · '}
                      {t('setupCard.products.keptOut', { count: excludedTotal })}
                    </span>
                  ) : null}
                </>
              }
              title={t('factsRail.filtersTitle', { count: filters.length })}
              items={filters.map((row) => ({
                key: row.key,
                label: `${filterLabel(row.key)} ${row.values.join(' ')}`,
                row,
              }))}
              renderItem={(item) => {
                const excluded = excludedByFilter.get(item.row.key) ?? 0;
                return (
                  <span className="flex w-full items-start justify-between gap-3">
                    <span className="min-w-0">
                      <span className="text-foreground font-medium">
                        {filterLabel(item.row.key)}
                      </span>{' '}
                      <span className="text-muted-foreground">
                        {t(
                          item.row.values.length > 1
                            ? 'factsRail.filterIsAny'
                            : 'factsRail.filterIs',
                        )}
                      </span>{' '}
                      <span className="text-foreground break-words">
                        {item.row.values.join(', ')}
                      </span>
                    </span>
                    {excludedByFilter.size > 0 ? (
                      <span
                        className={cn(
                          'shrink-0 text-xs tabular-nums',
                          excluded > 0 ? 'text-error font-medium' : 'text-muted-foreground',
                        )}
                        title={t('factsRail.filterExcludedHint')}
                      >
                        {t('factsRail.excludedShort', { count: excluded })}
                      </span>
                    ) : null}
                  </span>
                );
              }}
              footer={excludedByFilter.size > 0 ? t('factsRail.filterCountsSource') : undefined}
            />
          ) : null}
          {productIds.length > 0 ? (
            <SettingPopoverRow
              testId={`${testId}-product-ids-button`}
              summary={t('setupCard.products.byId', { count: productIds.length })}
              title={t('factsRail.productIdsTitle', { count: productIds.length })}
              items={productIds.map((id) => ({ key: id, label: id }))}
              renderItem={(item) => <span className="font-mono text-xs">{item.label}</span>}
              headerAction={
                <CopyIconButton text={productIds.join('\n')} label={t('factsRail.copyAllIds')} />
              }
            />
          ) : null}
        </SetupRow>

        {fields.length > 0 ? (
          <SetupRow label={t('setupCard.fields.label')} testId={`${testId}-fields`}>
            <SettingPopoverRow
              testId={`${testId}-fields-button`}
              summary={fieldsAnswer}
              title={t('factsRail.fieldsTitle', { count: fields.length })}
              items={fields.map((code) => ({ key: code, label: humanizeCode(code) }))}
              renderItem={(item) => {
                const FieldIcon = fieldIconOf(item.key);
                return (
                  <span className="flex items-start gap-2" title={item.label}>
                    <FieldIcon
                      aria-hidden="true"
                      className="text-primary mt-0.5 size-3.5 shrink-0"
                    />
                    <span className="line-clamp-2 break-words">{item.label}</span>
                  </span>
                );
              }}
              groupOf={(item) => fieldGroupOf(item.key)}
              groupLabel={(group) => tRuns(`newRun.fieldGroups.${group}`)}
              columns={2}
            />
          </SetupRow>
        ) : null}

        {scoringKey ? (
          <SetupRow label={t('setupCard.scoring.label')} testId={`${testId}-scoring`}>
            <OnOff on={scoringKey !== 'none'}>{t(`setupCard.scoring.${scoringKey}`)}</OnOff>
          </SetupRow>
        ) : null}

        {descriptors !== undefined ? (
          <SetupRow label={t('setupCard.descriptors.label')} testId={`${testId}-descriptors`}>
            <OnOff on={descriptors}>
              {t(descriptors ? 'setupCard.descriptors.on' : 'setupCard.descriptors.off')}
            </OnOff>
          </SetupRow>
        ) : null}

        {recordSkipped !== undefined ? (
          <SetupRow
            label={t('setupCard.skipped.label')}
            hint={t('setupCard.skipped.hint')}
            testId={`${testId}-skipped`}
          >
            {t(recordSkipped ? 'setupCard.skipped.on' : 'setupCard.skipped.off')}
          </SetupRow>
        ) : null}

        {trigger || run?.experimentId || run?.parentRunId ? (
          <SetupRow label={t('setupCard.started.label')} testId={`${testId}-started`}>
            {run?.scheduleId ? (
              <Link
                to="/catalog-enrichment/schedules/$scheduleId"
                params={{ scheduleId: run.scheduleId }}
                data-testid={`${testId}-schedule-link`}
                className="text-primary cursor-pointer hover:underline"
              >
                {t('setupCard.started.SCHEDULE')}
              </Link>
            ) : trigger ? (
              t(`setupCard.started.${trigger}`, {
                defaultValue: catalogLabel(tCatalog, 'trigger', trigger),
              })
            ) : null}
            {run?.experimentId ? (
              <span className="block">
                {t('setupCard.started.forExperiment')}{' '}
                <Link
                  to="/catalog-enrichment/experiments/$experimentId"
                  params={{ experimentId: run.experimentId }}
                  title={run.experimentId}
                  data-testid={`${testId}-experiment-link`}
                  className="text-primary cursor-pointer hover:underline"
                >
                  {run.experimentName ?? run.experimentId}
                </Link>
              </span>
            ) : null}
            {run?.parentRunId ? <ParentRunLink parentRunId={run.parentRunId} /> : null}
          </SetupRow>
        ) : null}
      </dl>

      <Button
        asChild
        variant="outline"
        size="sm"
        className="w-fit cursor-pointer gap-1.5"
        title={t('setupCard.copyHint')}
      >
        <Link
          to="/catalog-enrichment/runs/new"
          search={{ copyFrom: runId }}
          data-testid={`${testId}-copy-link`}
        >
          <RefreshCw aria-hidden="true" className="size-3.5" />
          {t('setupCard.copy')}
        </Link>
      </Button>
    </section>
  );
}

/** One setting: the question in small muted text, its answer underneath, rows split by hairlines. */
function SetupRow({
  label,
  hint,
  testId,
  children,
}: {
  label: string;
  hint?: string;
  testId: string;
  children: ReactNode;
}) {
  return (
    <div data-testid={testId} className="flex flex-col gap-0.5 border-t py-2.5 first:border-t-0">
      <dt className="text-secondary-text text-xs font-medium" title={hint}>
        {label}
      </dt>
      <dd className="text-foreground text-sm leading-snug">{children}</dd>
    </div>
  );
}

/** An answer for an on/off setting: a check when on, a muted cross when off. */
function OnOff({ on, children }: { on: boolean; children: ReactNode }) {
  return (
    <span
      className={cn(
        'inline-flex items-start gap-1.5',
        on ? 'text-foreground' : 'text-secondary-text',
      )}
    >
      {on ? (
        <Check aria-hidden="true" className="text-foreground mt-0.5 size-3.5 shrink-0" />
      ) : (
        <X aria-hidden="true" className="text-muted-foreground/70 mt-0.5 size-3.5 shrink-0" />
      )}
      {children}
    </span>
  );
}

interface PopoverItem {
  key: string;
  /** What the search box matches against. */
  label: string;
}

interface SettingPopoverRowProps<T extends PopoverItem> {
  testId: string;
  summary: ReactNode;
  title: string;
  items: T[];
  renderItem: (item: T) => ReactNode;
  /** Grid columns for short items (fields); long items (filters, ids) stay one per line. */
  columns?: 1 | 2;
  headerAction?: ReactNode;
  footer?: string | undefined;
  /** Groups the list under headings (fields by Content / Classification / …), in group order. */
  groupOf?: (item: T) => NewRunFieldGroup;
  groupLabel?: (group: NewRunFieldGroup) => string;
}

/**
 * An answer that opens the full list (fields, filters, ids) in a popover beside the rail,
 * searchable once it is long. The answer line is the trigger, with a chevron to say it opens.
 */
function SettingPopoverRow<T extends PopoverItem>({
  testId,
  summary,
  title,
  items,
  renderItem,
  columns = 1,
  headerAction,
  footer,
  groupOf,
  groupLabel,
}: SettingPopoverRowProps<T>) {
  const { t } = useTranslation('catalog-enrichment-run');
  const [query, setQuery] = useState('');
  const needle = query.trim().toLowerCase();
  const matched = needle ? items.filter((i) => i.label.toLowerCase().includes(needle)) : items;
  const shown = matched.slice(0, RENDER_CAP);
  // Headed sections when the caller groups; one untitled section otherwise.
  const sections = groupOf
    ? NEW_RUN_FIELD_GROUP_ORDER.map((group) => ({
        key: group,
        group,
        rows: shown.filter((item) => groupOf(item) === group),
      })).filter((section) => section.rows.length > 0)
    : [{ key: 'all', group: null, rows: shown }];

  return (
    <Popover
      onOpenChange={(open) => {
        if (!open) setQuery('');
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          data-testid={testId}
          className="hover:bg-muted/60 -mx-2 h-auto w-[calc(100%+1rem)] cursor-pointer justify-between gap-2 px-2 py-1 text-left text-sm font-normal whitespace-normal"
        >
          <span className="text-foreground min-w-0">{summary}</span>
          <ChevronRight aria-hidden="true" className="text-muted-foreground size-4 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        side="left"
        align="start"
        collisionPadding={16}
        data-testid={`${testId}-popover`}
        className="flex w-[26rem] max-w-[calc(100vw-2rem)] flex-col gap-3 p-0"
      >
        <div className="flex items-center justify-between gap-2 border-b px-4 pt-3 pb-2">
          <h4 className="text-foreground text-sm font-semibold">{title}</h4>
          {headerAction}
        </div>
        {items.length > SEARCH_FROM ? (
          <div className="relative px-4">
            <Search
              aria-hidden="true"
              className="text-muted-foreground pointer-events-none absolute top-1/2 left-6.5 size-3.5 -translate-y-1/2"
            />
            <Input
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
              }}
              placeholder={t('factsRail.searchPlaceholder')}
              aria-label={t('factsRail.searchPlaceholder')}
              className="h-8 pl-8 text-sm"
            />
          </div>
        ) : null}
        <div className="flex max-h-80 flex-col gap-3 overflow-y-auto px-4 pb-3 text-sm">
          {sections.map((section) => {
            const GroupIcon = section.group ? FIELD_GROUP_ICON[section.group] : null;
            return (
              <div key={section.key} className="flex flex-col gap-1.5">
                {section.group && groupLabel ? (
                  <h5 className="text-muted-foreground flex items-center gap-1.5 text-xs font-medium tracking-wide uppercase">
                    {GroupIcon ? <GroupIcon aria-hidden="true" className="size-3.5" /> : null}
                    {groupLabel(section.group)}
                    <span className="tabular-nums">· {section.rows.length}</span>
                  </h5>
                ) : null}
                <ul
                  className={cn(
                    columns === 2 ? 'grid grid-cols-2 gap-x-4 gap-y-1.5' : 'flex flex-col gap-2',
                  )}
                >
                  {section.rows.map((item) => (
                    <li key={item.key} className="min-w-0">
                      {renderItem(item)}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
          {matched.length === 0 ? (
            <p className="text-muted-foreground text-xs">{t('factsRail.noMatch', { query })}</p>
          ) : null}
          {matched.length > shown.length ? (
            <p className="text-muted-foreground text-xs">
              {t('factsRail.renderCapped', { shown: shown.length, total: matched.length })}
            </p>
          ) : null}
        </div>
        {footer ? (
          <p className="text-muted-foreground border-t px-4 py-2 text-xs">{footer}</p>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}

/** The batch this one retried, by its name — the id stays on hover. */
function ParentRunLink({ parentRunId }: { parentRunId: string }) {
  const { t } = useTranslation('catalog-enrichment-run');
  const { data: parent } = useRun(parentRunId);
  const { title } = useRunTitle(parent, undefined);
  return (
    <span className="block">
      {t('setupCard.started.retryOf')}{' '}
      <Link
        to="/catalog-enrichment/runs/$runId"
        params={{ runId: parentRunId }}
        title={parentRunId}
        data-testid="catalog-enrichment-run-setup-parent-link"
        className="text-primary cursor-pointer hover:underline"
      >
        {title}
      </Link>
    </span>
  );
}
